import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Alert, Badge, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { loadReminderPreferences } from '@renenet/shared/src/actions/settings';
import { initCurrencyInfo, saveAggregationCurrency } from '@renenet/shared/src/actions/currencies';
import Tier from '@renenet/shared/src/constants/tiers';
import PremiumGate from '../PremiumGate';
import ReminderPreferencesForm from './ReminderPreferencesForm';

// ISO 4217 fiat currency codes — kept in sync with backend tier_utils.py FIAT_CODES
const FIAT_CODES = new Set([
  'AED','AFN','ALL','AMD','ANG','AOA','ARS','AUD','AWG','AZN',
  'BAM','BBD','BDT','BGN','BHD','BIF','BMD','BND','BOB','BOV',
  'BRL','BSD','BTN','BWP','BYN','BZD','CAD','CDF','CHE','CHF',
  'CHW','CLF','CLP','CNY','COP','COU','CRC','CUC','CUP','CVE',
  'CZK','DJF','DKK','DOP','DZD','EGP','ERN','ETB','EUR','FJD',
  'FKP','GBP','GEL','GHS','GIP','GMD','GNF','GTQ','GYD','HKD',
  'HNL','HRK','HTG','HUF','IDR','ILS','INR','IQD','IRR','ISK',
  'JMD','JOD','JPY','KES','KGS','KHR','KMF','KPW','KRW','KWD',
  'KYD','KZT','LAK','LBP','LKR','LRD','LSL','LYD','MAD','MDL',
  'MGA','MKD','MMK','MNT','MOP','MRU','MUR','MVR','MWK','MXN',
  'MXV','MYR','MZN','NAD','NGN','NIO','NOK','NPR','NZD','OMR',
  'PAB','PEN','PGK','PHP','PKR','PLN','PYG','QAR','RON','RSD',
  'RUB','RWF','SAR','SBD','SCR','SDG','SEK','SGD','SHP','SLL',
  'SOS','SRD','SSP','STN','SVC','SYP','SZL','THB','TJS','TMT',
  'TND','TOP','TRY','TTD','TWD','TZS','UAH','UGX','USD','USN',
  'UYI','UYU','UYW','UZS','VES','VND','VUV','WST','XAF','XAG',
  'XAU','XBA','XBB','XBC','XBD','XCD','XDR','XOF','XPD','XPF',
  'XPT','XSU','XTS','XUA','XXX','YER','ZAR','ZMW','ZWL'
]);

function mapStateToProps(state) {
  return {
    settingsInfo: state.settingsInfo,
    userInfo: state.userInfo,
    currencyInfo: state.currencyInfo,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    loadReminderPreferences: () => dispatch(loadReminderPreferences()),
    initCurrencyInfo: () => dispatch(initCurrencyInfo()),
    saveAggregationCurrency: (currency) => dispatch(saveAggregationCurrency(currency)),
  };
}

class Settings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedCurrency: props.currencyInfo.aggregationCurrency,
      saving: false,
      saved: false,
    };
    this.handleSave = this.handleSave.bind(this);
  }

  componentDidMount() {
    this.props.loadReminderPreferences();
    this.props.initCurrencyInfo();
  }

  async handleSave(e) {
    e.preventDefault();
    this.setState({ saving: true });
    await this.props.saveAggregationCurrency(this.state.selectedCurrency);
    this.setState({ saving: false, saved: true });
    setTimeout(() => this.setState({ saved: false }), 3000);
  }

  render() {
    const isPremium = this.props.userInfo && this.props.userInfo.tier === Tier.PREMIUM;
    const isFree = this.props.userInfo && this.props.userInfo.tier === Tier.FREE;
    const { loaded, reminderPreferences } = this.props.settingsInfo;
    const { aggregationCurrency, currencyRates } = this.props.currencyInfo;
    const { selectedCurrency, saving, saved } = this.state;

    const availableCurrencies = Object.keys(currencyRates)
      .filter(code => !isFree || FIAT_CODES.has(code))
      .sort();

    return (
      <Container>
        <Row>
          <Col>
            <h2>Settings</h2>
            {isPremium
              ? <Badge bg="primary">Premium Plan</Badge>
              : <Badge bg="warning" text="dark">Free Plan</Badge>
            }
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <h5>Display Currency</h5>
            <Form onSubmit={this.handleSave} className="mt-2">
              <Form.Select
                style={{ width: '200px' }}
                value={selectedCurrency}
                onChange={e => this.setState({ selectedCurrency: e.target.value })}
                disabled={availableCurrencies.length === 0}
                className="mb-3"
              >
                {availableCurrencies.length === 0 && (
                  <option value={aggregationCurrency}>{aggregationCurrency}</option>
                )}
                {availableCurrencies.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </Form.Select>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? 'Saving\u2026' : 'Save'}
              </Button>
              {saved && (
                <Alert variant="success" className="mt-2 d-inline-block ms-3 py-1 px-3">
                  Currency saved.
                </Alert>
              )}
            </Form>
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>Reminder Emails</h5>
            {!isPremium && (
              <PremiumGate message="Upgrade to Premium to set up reminder emails." />
            )}
            {isPremium && !loaded && (
              <Spinner animation="border" size="sm" />
            )}
            {isPremium && loaded && (
              <ReminderPreferencesForm reminderPreferences={reminderPreferences} />
            )}
          </Col>
        </Row>
      </Container>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
